import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
    fleetColumns,
    fleetColumnsFromRequest,
    formatFleetRows,
    formatFleetOnRequest,
    formatFleetExport,
    fleetExportDataTittles,
} from '../../../helpers/tableUtils'
import FilterModal from '../../../components/FilterModal/FilterModal'
import ExportDataButton from '../../../components/ExportData/ExportDataButton'
import Table from '../../../components/Table/Table'
import useGlobalLoading from '../../../hooks/useGlobalLoading'
import useFleet from '../../../hooks/useFleet'
import useAuth from '../../../hooks/useAuth'
import {
    getFormattFilters,
    getInitialFilters,
} from '../../../helpers/fleetFiltersUtil'
import { tagHotjarRecording } from '../../../helpers/util'
import { getAllFleetData, saveFleetData } from '../../../../../indexedDb/fleets'
import { useFetchState } from '../../../context/FetchStateContext'

const fetchedFleetListKeys = new Set()

const FleetTable = ({
    selectable,
    selectedItems,
    onSelectItems,
    isInCard = false,
    hasExport = false,
    title,
    subtitle,
    isCreateRequest = false,
    singleSelection = true,
    fetchOnceKey = null,
}) => {
    const { handleSearchFleet, fleetFilters } = useFleet()
    const { user } = useAuth()
    const locationDefaults = user?.locationPreferences
    const { setIsFetching, setDataFetched } = useFetchState()

    const [fleetList, setFleetList] = useState([])
    const [listSize, setListSize] = useState(0)
    const { changeLoading } = useGlobalLoading()
    const [localFleetFilters, setLocalFleetFilters] = useState(fleetFilters)
    const [showFleetList, setShowFleetList] = useState({})
    const [toggleSearch, setToggleSearch] = useState(false)
    const [filterModalOpen, setFilterModalOpen] = useState(false)
    const [hasActiveFilters, setHasActiveFilters] = useState(false)
    const hasMountedRef = React.useRef(false)

    const initialFilters = getInitialFilters(
        locationDefaults,
        localFleetFilters,
    )
    const filters = initialFilters

    useEffect(() => {
        if (filters) {
            const hasActiveFilters = filters.some(group =>
                group.filters.some(filter => filter.checked),
            )
            setHasActiveFilters(hasActiveFilters)
        }
    }, [filters])

    const handleToggleSearch = () => {
        setToggleSearch(!toggleSearch)
    }

    const handleFilters = filters => {
        const copyFilters = getFormattFilters(filters)
        setLocalFleetFilters(copyFilters)
        handleToggleSearch()
    }

    const onFilterSuccess = async response => {
        let showList = showFleetList
        if (Object.keys(showList).length === 0) {
            Object.keys(response?.data?.fleet).forEach(key => {
                showList[key] = true
            })
        }
        setFleetList(response?.data?.fleet)
        await saveFleetData(response?.data?.fleet)
        setListSize(response?.data?.fleet.length)
        changeLoading(false)
        setShowFleetList(showList)
        setIsFetching(false)
        setDataFetched(true)
    }

    const loadFleetData = async () => {
        try {
            const storedFleetData = await getAllFleetData()
            if (storedFleetData && storedFleetData.length > 0) {
                setFleetList(storedFleetData)
                setListSize(storedFleetData.length)
                syncOfflineSelectedItems(storedFleetData)
            } else {
                console.warn('No fleet data found in IndexedDB')
            }
        } catch (error) {
            console.error('Error loading fleet data from IndexedDB:', error)
        }
    }

    const syncOfflineSelectedItems = storedData => {
        if (!selectedItems?.length || !storedData?.length) {
            return
        }

        const updatedItems = selectedItems
            .map(item => {
                const match = storedData.find(
                    fleet =>
                        fleet.salesforceID === item.salesforceID ||
                        fleet.customId === item.customId,
                )
                return match || item
            })
            .filter(Boolean)

        const currentIds = selectedItems
            .map(item => item.salesforceID || item.customId)
            .sort()
        const updatedIds = updatedItems
            .map(item => item.salesforceID || item.customId)
            .sort()

        if (currentIds.join(',') !== updatedIds.join(',')) {
            onSelectItems(updatedItems)
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await loadFleetData()

            const isInitialMount = !hasMountedRef.current
            hasMountedRef.current = true

            const shouldSkipInitialFetch =
                isInitialMount &&
                fetchOnceKey &&
                fetchedFleetListKeys.has(fetchOnceKey)

            if (shouldSkipInitialFetch) {
                setIsFetching(false)
                setDataFetched(true)
                return
            }

            if (isInitialMount && fetchOnceKey) {
                fetchedFleetListKeys.add(fetchOnceKey)
            }

            filter()
        }

        loadData()
    }, [toggleSearch])

    useEffect(() => {
        if (fleetList?.length > 0 && selectedItems?.length > 0) {
            syncOfflineSelectedItems(fleetList)
        }
    }, [fleetList, selectedItems])

    useEffect(() => {
        const saveData = async () => {
            if (fleetList) {
                await saveFleetData(fleetList)
            }
        }
        saveData()
    }, [fleetList])

    const onFilterError = () => {
        tagHotjarRecording('API Error')
        changeLoading(false)
        setFleetList([])
        setListSize(0)
        setIsFetching(false)
        setDataFetched(true)
    }

    const filter = () => {
        changeLoading(true)
        setIsFetching(true)
        const filterInfo = {
            query: '',
            categories: localFleetFilters,
            user: user?.id,
        }
        handleSearchFleet(filterInfo, onFilterSuccess, onFilterError)
    }

    const userRows = isCreateRequest
        ? formatFleetOnRequest(fleetList)
        : formatFleetRows(fleetList)

    const tableColumns = isCreateRequest
        ? fleetColumnsFromRequest
        : fleetColumns

    const fleetExportData = formatFleetExport(fleetList)

    return (
        <Table
            isInCard={isInCard}
            selectable={selectable}
            hasSearch={true}
            selectedItems={selectedItems}
            onSelectItem={onSelectItems}
            columns={tableColumns}
            data={userRows}
            setRowsSize={setListSize}
            totalResults={userRows?.length}
            type="scroll"
            onRowClick={onSelectItems}
            singleSelection={singleSelection}
            headerTitle={
                title
                    ? title
                    : !selectable
                    ? 'Fleet Details'
                    : 'Select the assets you’d like to remove'
            }
            headerSubtitle={
                subtitle
                    ? subtitle
                    : !selectable && (
                          <span>
                              You have a total of{' '}
                              <b className="text-black90">{listSize}</b> assets
                          </span>
                      )
            }
            searchPlaceholder="Search"
            actions={[
                <FilterModal
                    key="filterModal"
                    filtersGroup={filters}
                    open={filterModalOpen}
                    setOpen={setFilterModalOpen}
                    onFilter={handleFilters}
                    isSmallScreen
                    data={userRows}
                />,
                hasExport && (
                    <ExportDataButton
                        isButtonSmallMD={true}
                        key="exportData"
                        text="Export Data"
                        fileName="Fleet Table.xlsx"
                        sheetName="FleetTable"
                        data={fleetExportData}
                        tittles={fleetExportDataTittles}
                    />
                ),
            ]}
            hasActiveFilters={hasActiveFilters}
            searchFields={[
                'customerAlias',
                'name',
                'serialLotNumber',
                'equipmentNumber',
                'fleetName',
                'fleetAddress',
                'manufacturer',
                'simpleType',
                'categoryType',
            ]}
        />
    )
}

FleetTable.propTypes = {
    selectable: PropTypes.bool,
    selectedItems: PropTypes.array,
    onSelectItems: PropTypes.func,
    isInCard: PropTypes.bool,
    hasExport: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
    isCreateRequest: PropTypes.bool,
    singleSelection: PropTypes.bool,
    fetchOnceKey: PropTypes.string,
}

export default FleetTable
